import React from 'react';
import {
  Fade, TextField, Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { formatRut } from 'react-rut-formatter';
import useStyles from './styles';
import { setRut } from '../../../../../../utils/helpers';
import { rutValidationSchema } from '../../../validationSchemas';
import ZubaleUserInputForm from '../../../../ZubaleUserInputForm';
import type { PLRFormProps } from '../../../../../../types/preloan';
import type { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import TalanaTermsAndConditions from '../../../../TalanaTermsAndConditions';
import analytics from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';

export default function RutForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleNext,
    handleEnableLoading,
    handleCurrentData,
  } = props;
  const classes = useStyles();
  React.useEffect(() => handleEnableLoading(false), []);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [recaptchaCheckError, setRecaptchaCheckError] = React.useState(false);

  const [zubaleUserInput, setZubaleUserInput] = React.useState(formSchema.userInput);

  const shouldCallBNPL = false;
  const invisibleRecaptchaRef = React.useRef<ReCAPTCHA>(null);

  const handleZubaleUserInput = (userInput: FormSchemaV3['userInput']) => {
    setZubaleUserInput(userInput);
  };

  React.useEffect(() => {
    analytics.page(
      inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING'),
      'INGRESO RUT',
    );
  }, []);

  const recaptchaOnChange = () => {
    analytics.track('Click Checkbox', {
      texto: 'No soy un robot',
      initiator: 'usuario',
    });
  };
  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      validationSchema={rutValidationSchema}
      onSubmit={async (values) => {
        handleEnableLoading(true);
        setRecaptchaCheckError(false);
        let invisibleRecaptchaValue;
        if (invisibleRecaptchaRef.current && shouldCallBNPL) {
          await invisibleRecaptchaRef.current?.executeAsync();
          invisibleRecaptchaValue = invisibleRecaptchaRef.current?.getValue();
          if (!invisibleRecaptchaValue) {
            setRecaptchaCheckError(true);
            return handleEnableLoading(false);
          }
        }
        let recaptchaValue;
        if (recaptchaRef.current) {
          recaptchaValue = recaptchaRef.current.getValue();
          if (!recaptchaValue) {
            setRecaptchaCheckError(true);
            return handleEnableLoading(false);
          }
        }
        handleCurrentData({
          ...values,
          captchaToken: recaptchaValue,
          invisibleCaptchaToken: invisibleRecaptchaValue,
          origin: values.origin,
          userInput: zubaleUserInput,
          rut: formatRut(values.rut).toLocaleLowerCase(),
          shouldCallBNPL,
        });
        return handleNext();
      }}
    >
      {({
        values, handleBlur, touched, errors, setFieldValue,
      }) => (
        <Fade
          in
          {...({ timeout: 1000 })}
        >
          <Form>
            <Typography variant="h6" align="center" gutterBottom>
              Por favor ingresa tu RUT
            </Typography>
            <br />
            <TextField
              className={classes.textInput}
              size="small"
              variant="outlined"
              autoComplete="off"
              placeholder="RUT"
              focused
              inputProps={{ style: { textAlign: 'center' } }}
              id="rut"
              name="rut"
              data-pw="preloan-rut-input"
              autoFocus
              error={touched.rut && Boolean(errors.rut)}
              helperText={touched.rut && errors.rut ? errors.rut : '  '}
              FormHelperTextProps={{
                style: {
                  textAlign: 'center',
                  height: 10,
                },
              }}
              value={values.rut}
              onChange={(e) => setRut(setFieldValue, e.target.value)}
              onBlur={(e) => {
                setRut(setFieldValue, values.rut);
                handleBlur(e);
              }}
            />
            <br />
            <br />
            <ReCAPTCHA
              onChange={recaptchaOnChange}
              style={{
                display: 'flex', width: '100%', justifyContent: 'center', marginTop: 20,
              }}
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_PLRV2 as string}
            />
            {shouldCallBNPL && (
              <ReCAPTCHA
                size="invisible"
                ref={invisibleRecaptchaRef}
                sitekey={process.env.REACT_APP_INVISIBLE_RECAPTCHA_SITE_KEY as string}
              />
            )}
            {recaptchaCheckError && <Typography color="error" variant="caption">Queremos saber si no eres un robot </Typography>}
            <br />
            {values.origin === 'zubale'
              && (
              <ZubaleUserInputForm
                formSchema={formSchema}
                onSubmit={handleZubaleUserInput}
              />
              )}
            {values.origin === 'talana' && !values.optIn
              && <TalanaTermsAndConditions />}
          </Form>
        </Fade>
      )}

    </Formik>
  );
}
